import React from 'react';
import { CheckCircle2, Clock3, Eye, FileText, RotateCcw, Upload } from 'lucide-react';
import Button from '../../../components/ui/Button';

const STATUS = {
  awaiting_trainer: ['Awaiting trainer', 'bg-amber-100 text-amber-800'],
  awaiting_department: ['Awaiting department', 'bg-blue-100 text-blue-800'],
  awaiting_final: ['Awaiting final approval', 'bg-violet-100 text-violet-800'],
  approved: ['Approved', 'bg-emerald-100 text-emerald-800'],
  rejected: ['Returned', 'bg-red-100 text-red-800']
};

const verificationDate = value => value
  ? new Date(value).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })
  : '';

const POESubmissionCard = ({ submission, onOpen, onView, onUpload, allowExternalVerify = false, sequence = null }) => {
  const hasEvidence = Boolean(submission.evidence_id);
  // The server field is only a capability hint. The caller must explicitly
  // confirm that the signed-in user is the dedicated external verifier before
  // exposing an external verification action.
  const canExternallyVerify = allowExternalVerify && Boolean(submission.external_can_verify ?? submission.can_verify);
  const [statusLabel, statusTone] = hasEvidence
    ? (STATUS[submission.workflow_stage] || ['In progress', 'bg-slate-100 text-slate-700'])
    : ['Ready to upload', 'bg-slate-100 text-slate-700'];

  return (
    <article className="rounded-xl border bg-white p-5 shadow-sm transition hover:border-slate-300">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {sequence !== null && <span className="grid h-6 min-w-6 place-items-center rounded-full bg-slate-100 px-1.5 text-[11px] font-bold text-slate-600">{sequence}</span>}
            <h3 className="font-bold text-slate-950">{submission.unit_code} · CAM {submission.assessment_number}</h3>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusTone}`}>{statusLabel}</span>
            {submission.read_only && !canExternallyVerify && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">View only</span>}
          </div>
          <p className="mt-1 text-sm text-slate-600">{submission.unit_name}</p>
          <p className="mt-1 text-xs text-slate-500">{submission.student_name} · {submission.student_number || 'Student number unavailable'}</p>
        </div>
        <div className="flex items-center gap-2">
          {submission.can_upload && (
            <Button onClick={() => onUpload(submission)} className="h-9 text-xs">
              {submission.workflow_stage === 'rejected' ? <RotateCcw className="mr-1.5 h-4 w-4" /> : <Upload className="mr-1.5 h-4 w-4" />}
              {submission.workflow_stage === 'rejected' ? 'Upload correction' : 'Upload script'}
            </Button>
          )}
          {hasEvidence && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => (onView ? onView(submission) : onOpen(submission))} className="h-9 text-xs">
                <Eye className="mr-1.5 h-4 w-4" />View
              </Button>
              {(submission.can_review || canExternallyVerify) && (
                <Button onClick={() => onOpen(submission)} className="h-9 text-xs">
                  {canExternallyVerify ? 'Verify' : 'Review'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-2 border-t pt-3 text-xs text-slate-500 sm:grid-cols-3">
        <span><b className="text-slate-700">Programme:</b> {submission.programme_name || 'Not assigned'}</span>
        <span><b className="text-slate-700">Department:</b> {submission.department_name || 'Not assigned'}</span>
        <span><b className="text-slate-700">Mark:</b> {submission.raw_marks ?? '—'}</span>
      </div>

      {hasEvidence && (
        <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            {submission.workflow_stage === 'approved' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Clock3 className="h-4 w-4 text-slate-400" />}
            <FileText className="h-4 w-4 text-slate-400" />
            <span className="min-w-0 flex-1 truncate">{submission.file_name}</span>
            {submission.uploaded_at && <span className="whitespace-nowrap">{new Date(submission.uploaded_at).toLocaleDateString('en-KE')}</span>}
          </div>
          {(submission.trainer_verified_at || submission.external_verified_at) && (
            <div className="mt-2 space-y-0.5 border-t border-slate-200 pt-2 text-[11px]">
              {submission.trainer_verified_at && <p><b className="text-slate-700">Internally verified by:</b> {submission.trainer_name || 'Trainer'} · {verificationDate(submission.trainer_verified_at)}</p>}
              {submission.external_verified_at && <p><b className="text-slate-700">Externally verified by:</b> {submission.external_verifier_name || 'External verifier'} · {verificationDate(submission.external_verified_at)}</p>}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default POESubmissionCard;
